/**
 * Shared API helper utilities for CSRF tokens and HTTP headers.
 *
 * Consolidates duplicated CSRF/headers logic that was previously defined
 * independently in taskApi.ts, favoritesApi.ts, ProjectRail.svelte,
 * TaskViewOptions.svelte, and Projects/Show.svelte.
 */

/**
 * Read the Rails CSRF token from the meta tag in the document head.
 * Returns empty string if the meta tag is not found.
 */
export function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute('content') || '';
}

/**
 * Standard JSON request headers including CSRF token.
 * Suitable for all fetch-based API calls to the Rails backend.
 */
export function jsonHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-CSRF-Token': getCsrfToken(),
    'X-Requested-With': 'XMLHttpRequest',
  };
}

/**
 * Extract a user-facing message from a failed API response.
 * Supports `{ error: string }`, `{ error: { message } }`, and `{ errors: { field } }`.
 */
export async function readApiErrorMessage(
  response: Response,
  fallback = 'Server error',
): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.error === 'string' && body.error.trim()) return body.error;
    if (typeof body?.error?.message === 'string' && body.error.message.trim()) {
      return body.error.message;
    }
    if (body?.errors && typeof body.errors === 'object') {
      const first = Object.values(body.errors)
        .flat()
        .find((value) => typeof value === 'string' && value.trim());
      if (typeof first === 'string') return first;
    }
  } catch {
    // Non-JSON bodies fall through to the fallback.
  }
  return fallback;
}
